'use client'

import * as React from 'react'
import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

interface MobileNavProps {
  showBilling: boolean
  billingBadge?: boolean
  displayName: string | null
}

export function MobileNav({ showBilling, billingBadge = false, displayName }: MobileNavProps) {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()

  const links: Array<{ href: string; label: string; badge?: boolean }> = [
    { href: '/stories', label: 'Stories' },
    { href: '/book', label: 'Book' },
    { href: '/settings', label: 'Family' },
    ...(showBilling
      ? [{ href: '/billing', label: 'Billing', badge: billingBadge }]
      : []),
  ]

  return (
    <div className="md:hidden">
      {/* Hamburger button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative flex flex-col justify-center items-center w-10 h-10 gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        <span className={`block w-5 h-px bg-ink transition-all duration-300 ${open ? 'rotate-45 translate-y-[3.5px]' : ''}`} />
        <span className={`block w-5 h-px bg-ink transition-all duration-300 ${open ? 'opacity-0' : ''}`} />
        <span className={`block w-5 h-px bg-ink transition-all duration-300 ${open ? '-rotate-45 -translate-y-[3.5px]' : ''}`} />
        {showBilling && billingBadge && !open && (
          <span
            aria-label="Billing needs attention"
            className="absolute top-1 right-1 inline-block w-1 h-1 rounded-full bg-amber-500"
          />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 bg-cream border-b border-subtle z-50 px-6 py-4">
          <nav className="flex flex-col gap-1">
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className={`relative font-serif text-sm py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${
                  pathname === link.href
                    ? 'text-gold'
                    : 'text-text-muted hover:text-gold'
                }`}
              >
                {link.label}
                {link.badge && (
                  <span
                    aria-label="Needs attention"
                    className="ml-2 inline-block w-1 h-1 rounded-full bg-amber-500 align-middle"
                  />
                )}
              </Link>
            ))}
          </nav>
          {displayName && (
            <div className="border-t border-subtle mt-3 pt-3">
              <p className="font-serif text-xs text-text-muted">{displayName}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
